import { unzipSync, strFromU8 } from "fflate";
import type { AccountSnapshot, PlatformId, PostMetric } from "../types";
import { parsePlatform } from "./platforms";

export interface ParsedTable {
  headers: string[];
  rows: string[][];
}

export interface ImportIssue {
  row: number;
  message: string;
}

export interface ParsedImport {
  snapshots: AccountSnapshot[];
  posts: PostMetric[];
  errors: ImportIssue[];
  duplicates: ImportDuplicate[];
}

export interface ImportDuplicate {
  kind: "snapshot" | "post";
  row: number;
  key: string;
  existingId: string;
}

const HEADER_ALIASES: Record<string, string> = {
  日期: "date",
  记录日期: "date",
  date: "date",
  平台: "platform",
  platform: "platform",
  粉丝: "followers",
  粉丝数: "followers",
  followers: "followers",
  发帖数: "posts",
  帖子数: "posts",
  posts: "posts",
  浏览: "views",
  浏览量: "views",
  播放: "views",
  播放量: "views",
  views: "views",
  点赞: "likes",
  点赞数: "likes",
  likes: "likes",
  收藏: "saves",
  收藏数: "saves",
  saves: "saves",
  评论: "comments",
  评论数: "comments",
  comments: "comments",
  标题: "title",
  帖子标题: "title",
  title: "title",
  链接: "url",
  发布链接: "url",
  url: "url",
  选题: "topicId",
  选题id: "topicId",
  topicid: "topicId",
  类型: "recordType",
  type: "recordType",
};

export function parseCsv(text: string): ParsedTable {
  const source = text.charCodeAt(0) === 0xfeff ? text.slice(1) : text;
  const rows: string[][] = [];
  let row: string[] = [];
  let cell = "";
  let inQuotes = false;
  for (let index = 0; index < source.length; index += 1) {
    const char = source[index];
    if (inQuotes) {
      if (char === '"') {
        if (source[index + 1] === '"') {
          cell += '"';
          index += 1;
        } else {
          inQuotes = false;
        }
      } else {
        cell += char;
      }
      continue;
    }
    if (char === '"') {
      inQuotes = true;
      continue;
    }
    if (char === ",") {
      row.push(cell.trim());
      cell = "";
      continue;
    }
    if (char === "\n") {
      row.push(cell.trim());
      cell = "";
      if (row.some((value) => value.length > 0)) rows.push(row);
      row = [];
      continue;
    }
    if (char === "\r") continue;
    cell += char;
  }
  if (cell.length > 0 || row.length > 0) {
    row.push(cell.trim());
    if (row.some((value) => value.length > 0)) rows.push(row);
  }
  const [headers = [], ...body] = rows;
  return { headers, rows: body };
}

export function parseXlsx(data: Uint8Array): ParsedTable {
  const files = unzipSync(data);
  const sheet = findSheet(files);
  const shared = readSharedStrings(files);
  const rows = new Map<number, string[]>();
  const rowPattern = /<row\b([^>]*)>([\s\S]*?)<\/row>/g;
  const cellPattern = /<c\b([^>]*)>([\s\S]*?)<\/c>/g;
  let rowMatch: RegExpExecArray | null;
  while ((rowMatch = rowPattern.exec(sheet))) {
    const rowIndex = Number(attribute(rowMatch[1] ?? "", "r") ?? rows.size + 1);
    const cells: string[] = [];
    let cellMatch: RegExpExecArray | null;
    const rowXml = rowMatch[2] ?? "";
    cellPattern.lastIndex = 0;
    while ((cellMatch = cellPattern.exec(rowXml))) {
      const attrs = cellMatch[1] ?? "";
      const body = cellMatch[2] ?? "";
      const ref = attribute(attrs, "r") ?? "";
      const column = columnIndex(ref);
      const type = attribute(attrs, "t");
      cells[column] = readCell(body, type, shared);
    }
    rows.set(rowIndex, cells.map((value) => value ?? ""));
  }
  const ordered = [...rows.entries()]
    .sort((a, b) => a[0] - b[0])
    .map((entry) => entry[1]);
  const [headers = [], ...body] = ordered.filter((row) => row.some((value) => value.trim().length > 0));
  return { headers: headers.map((value) => value.trim()), rows: body.map((row) => row.map((value) => value.trim())) };
}

export function snapshotDuplicateKey(row: { platform: string; recordedDate: string }): string {
  return `${row.platform}|${row.recordedDate}`;
}

export function postDuplicateKey(row: { platform: string; url: string; title: string; recordedDate: string }): string {
  const url = row.url.trim().toLowerCase();
  if (url) return `${row.platform}|url|${url}`;
  return `${row.platform}|title|${row.title.trim().toLowerCase()}|${row.recordedDate}`;
}

export function planImport(input: {
  table: ParsedTable;
  now: string;
  idFactory: () => string;
  snapshots: AccountSnapshot[];
  posts: PostMetric[];
}): ParsedImport {
  const columns = input.table.headers.map((header) => HEADER_ALIASES[header.trim().toLowerCase()] ?? HEADER_ALIASES[header.trim()] ?? "");
  if (!columns.includes("date") || !columns.includes("platform")) {
    return {
      snapshots: [],
      posts: [],
      duplicates: [],
      errors: [{ row: 1, message: "表头需要至少包含「日期」和「平台」。" }],
    };
  }
  const snapshots: AccountSnapshot[] = [];
  const posts: PostMetric[] = [];
  const errors: ImportIssue[] = [];
  const duplicates: ImportDuplicate[] = [];
  const seenSnapshots = new Map(
    input.snapshots.filter((item) => item.deletedAt == null).map((item) => [snapshotDuplicateKey(item), item.id]),
  );
  const seenPosts = new Map(
    input.posts.filter((item) => item.deletedAt == null).map((item) => [postDuplicateKey(item), item.id]),
  );

  input.table.rows.forEach((row, index) => {
    const rowNumber = index + 2;
    const record = Object.fromEntries(columns.map((key, column) => [key, row[column] ?? ""])) as Record<string, string>;
    if (Object.values(record).every((value) => !value.trim())) return;
    const platform = parsePlatform(record.platform);
    const date = normalizeDate(record.date ?? "");
    if (!platform) {
      errors.push({ row: rowNumber, message: `无法识别平台「${record.platform || "空"}」。` });
      return;
    }
    if (!date) {
      errors.push({ row: rowNumber, message: `日期「${record.date || "空"}」需要是 YYYY-MM-DD。` });
      return;
    }
    const numbers = readNumbers(record, rowNumber, errors);
    if (!numbers) return;
    const wantsPost =
      (record.recordType ?? "").includes("帖") ||
      Boolean(record.title?.trim() || record.url?.trim() || record.topicId?.trim());
    if (wantsPost) {
      const post: PostMetric = {
        id: input.idFactory(),
        platform,
        topicId: record.topicId?.trim() || null,
        title: record.title?.trim() || "",
        url: record.url?.trim() || "",
        recordedDate: date,
        views: numbers.views ?? null,
        likes: numbers.likes ?? null,
        saves: numbers.saves ?? null,
        comments: numbers.comments ?? null,
        updatedAt: input.now,
        deletedAt: null,
      };
      const key = postDuplicateKey(post);
      const existingId = seenPosts.get(key);
      if (existingId) duplicates.push({ kind: "post", row: rowNumber, key, existingId });
      seenPosts.set(key, existingId ?? post.id);
      posts.push(post);
      return;
    }
    const snapshot: AccountSnapshot = {
      id: input.idFactory(),
      platform,
      recordedDate: date,
      followers: numbers.followers ?? null,
      posts: numbers.posts ?? null,
      views: numbers.views ?? null,
      likes: numbers.likes ?? null,
      saves: numbers.saves ?? null,
      comments: numbers.comments ?? null,
      updatedAt: input.now,
      deletedAt: null,
    };
    const key = snapshotDuplicateKey(snapshot);
    const existingId = seenSnapshots.get(key);
    if (existingId) duplicates.push({ kind: "snapshot", row: rowNumber, key, existingId });
    seenSnapshots.set(key, existingId ?? snapshot.id);
    snapshots.push(snapshot);
  });

  return { snapshots, posts, errors, duplicates };
}

export function blankTemplateCsv(): string {
  return "日期,平台,粉丝,发帖数,浏览,点赞,收藏,评论,标题,链接,类型\n";
}

function readNumbers(
  record: Record<string, string>,
  row: number,
  errors: ImportIssue[],
): Partial<Record<"followers" | "posts" | "views" | "likes" | "saves" | "comments", number | null>> | null {
  const keys = ["followers", "posts", "views", "likes", "saves", "comments"] as const;
  const result: Partial<Record<(typeof keys)[number], number | null>> = {};
  for (const key of keys) {
    const raw = record[key]?.trim() ?? "";
    if (!raw) {
      result[key] = null;
      continue;
    }
    const value = Number(raw.replace(/,/g, ""));
    if (!Number.isFinite(value)) {
      errors.push({ row, message: `「${raw}」不是数字。` });
      return null;
    }
    result[key] = value;
  }
  return result;
}

export function normalizeDate(value: string): string | null {
  const trimmed = value.trim();
  const match = /^(\d{4})[-/.](\d{1,2})[-/.](\d{1,2})/.exec(trimmed);
  if (!match) return null;
  const year = match[1];
  const month = match[2]?.padStart(2, "0");
  const day = match[3]?.padStart(2, "0");
  if (!year || !month || !day) return null;
  const monthNumber = Number(month);
  const dayNumber = Number(day);
  if (monthNumber < 1 || monthNumber > 12 || dayNumber < 1 || dayNumber > 31) return null;
  return `${year}-${month}-${day}`;
}

function findSheet(files: Record<string, Uint8Array>): string {
  const named = files["xl/worksheets/sheet1.xml"];
  if (named) return strFromU8(named);
  const key = Object.keys(files).find((item) => /xl\/worksheets\/sheet\d+\.xml$/.test(item));
  if (!key || !files[key]) throw new Error("这个 xlsx 里没有工作表。");
  return strFromU8(files[key]);
}

function readSharedStrings(files: Record<string, Uint8Array>): string[] {
  const file = files["xl/sharedStrings.xml"];
  if (!file) return [];
  const xml = strFromU8(file);
  const items: string[] = [];
  const pattern = /<si\b[^>]*>([\s\S]*?)<\/si>/g;
  let match: RegExpExecArray | null;
  while ((match = pattern.exec(xml))) {
    const texts = [...(match[1] ?? "").matchAll(/<t\b[^>]*>([\s\S]*?)<\/t>/g)].map((item) =>
      decodeXml(item[1] ?? ""),
    );
    items.push(texts.join(""));
  }
  return items;
}

function readCell(body: string, type: string | null, shared: string[]): string {
  if (type === "inlineStr") {
    return [...body.matchAll(/<t\b[^>]*>([\s\S]*?)<\/t>/g)].map((item) => decodeXml(item[1] ?? "")).join("");
  }
  const value = /<v>([\s\S]*?)<\/v>/.exec(body)?.[1] ?? "";
  if (type === "s") return shared[Number(value)] ?? "";
  return decodeXml(value);
}

function attribute(source: string, name: string): string | null {
  const match = new RegExp(`${name}="([^"]*)"`).exec(source);
  return match?.[1] ?? null;
}

function columnIndex(ref: string): number {
  const letters = ref.replace(/[0-9]/g, "");
  if (!letters) return 0;
  let index = 0;
  for (const char of letters) index = index * 26 + (char.toUpperCase().charCodeAt(0) - 64);
  return index - 1;
}

function decodeXml(value: string): string {
  return value
    .replace(/&lt;/g, "<")
    .replace(/&gt;/g, ">")
    .replace(/&quot;/g, '"')
    .replace(/&apos;/g, "'")
    .replace(/&#(\d+);/g, (_, code: string) => String.fromCodePoint(Number(code)))
    .replace(/&amp;/g, "&");
}

export function isKnownPlatform(value: string): value is PlatformId {
  return parsePlatform(value) != null;
}
